import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MAX_MONTHS_INCLUSIVE = 12; // inclusive => start to end spans <= 12 months

function parseYm(ym) {
  if (!ym) return null;
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return null;
  return new Date(y, m - 1, 1);
}
function ymKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}
function addMonths(date, diff) {
  return new Date(date.getFullYear(), date.getMonth() + diff, 1);
}
function monthsDiff(a, b) {
  return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
}
function clampRange(a, b) {
  const da = parseYm(a);
  const db = parseYm(b);
  if (!da || !db) return { from: a || null, to: b || null };
  return da <= db ? { from: a, to: b } : { from: b, to: a };
}
function formatDisplay(from, to) {
  if (!from || !to) return '';
  const [fy, fm] = from.split('-').map(Number);
  const [ty, tm] = to.split('-').map(Number);
  return `${String(fm).padStart(2, '0')}/${fy} – ${String(tm).padStart(2, '0')}/${ty}`;
}

export default function MonthRangePicker({ value, onChange, placeholder = 'MM/YYYY – MM/YYYY' }) {
  const committedFrom = value?.from ?? null;
  const committedTo = value?.to ?? null;

  const [isOpen, setIsOpen] = useState(false);
  const [tempFrom, setTempFrom] = useState(committedFrom);
  const [tempTo, setTempTo] = useState(committedTo);

  // Current month (no future allowed)
  const today = new Date();
  const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  // Initial boards: adjacent years
  const baseYear = (committedFrom && parseYm(committedFrom)?.getFullYear()) || today.getFullYear();
  const [leftYear, setLeftYear] = useState(baseYear);
  const [rightYear, setRightYear] = useState(baseYear + 1);

  // Keep temp and boards synced when the popup closes/opens
  useEffect(() => {
    if (!isOpen) {
      setTempFrom(committedFrom);
      setTempTo(committedTo);
      const base = (committedFrom && parseYm(committedFrom)?.getFullYear()) || today.getFullYear();
      setLeftYear(base - 1);
      setRightYear(base + 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Close on outside click
  const rootRef = useRef(null);
  useEffect(() => {
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const display = useMemo(() => formatDisplay(committedFrom, committedTo), [committedFrom, committedTo]);

  // Rules for disabling a month
  const isMonthDisabled = (y, mIdx) => {
    const candidate = new Date(y, mIdx, 1);

    // 1) No future months
    if (candidate > currentMonth) return true;

    // 2) If choosing the end after a start, enforce max 12 months inclusive
    if (tempFrom && !tempTo) {
      const fromD = parseYm(tempFrom);
      if (fromD) {
        const diff = Math.abs(monthsDiff(fromD, candidate));
        if (diff > (MAX_MONTHS_INCLUSIVE - 1)) return true;
      }
    }
    return false;
  };

  const pickMonth = (year, monthIdx) => {
    if (isMonthDisabled(year, monthIdx)) return;

    const key = `${year}-${String(monthIdx + 1).padStart(2, '0')}`;

    if (!tempFrom || (tempFrom && tempTo)) {
      // start a new selection
      setTempFrom(key);
      setTempTo(null);
    } else {
      // finish selection; clamp to 12 months inclusive if needed
      const { from, to } = clampRange(tempFrom, key);
      const fromD = parseYm(from);
      const toD = parseYm(to);
      if (!fromD || !toD) return;

      const diff = monthsDiff(fromD, toD);
      if (diff > (MAX_MONTHS_INCLUSIVE - 1)) {
        const clampedTo = addMonths(fromD, MAX_MONTHS_INCLUSIVE - 1);
        setTempFrom(from);
        setTempTo(ymKey(clampedTo));
      } else {
        setTempFrom(from);
        setTempTo(to);
      }
    }
  };

  // Navigation handlers that keep years adjacent
  const shiftLeftYear = (delta) => {
    const ny = leftYear + delta;
    setLeftYear(ny);
    setRightYear(ny + 1);
  };
  const shiftRightYear = (delta) => {
    const ny = rightYear + delta;
    setRightYear(ny);
    setLeftYear(ny - 1);
  };

  const setPreset = (monthsBack) => {
    // end = current month; start = end - (monthsBack - 1)
    const end = currentMonth;
    const start = addMonths(end, -(monthsBack - 1));
    const startYear = start.getFullYear();

    setTempFrom(ymKey(start));
    setTempTo(ymKey(end));

    // Adjust boards to show the start year on the left and the adjacent year on the right
    setLeftYear(startYear);
    setRightYear(startYear + 1);
  };

  const apply = () => {
    if (tempFrom && tempTo) {
      onChange?.({ from: tempFrom, to: tempTo });
      setIsOpen(false);   // Apply still closes popup
    }
  };
  
  const reset = () => {
    setTempFrom(null);
    setTempTo(null);
    onChange?.(null);     // Clear selection, notify parent
    // Popup remains open
  };
  

  const monthBtnClass = (y, idx) => {
    const key = `${y}-${String(idx + 1).padStart(2, '0')}`;
    const fromD = parseYm(tempFrom);
    const toD = parseYm(tempTo);
    const isStart = tempFrom && key === tempFrom;
    const isEnd = tempTo && key === tempTo;
    let inRange = false;
    if (fromD && toD) {
      const d = new Date(y, idx, 1);
      inRange = d >= fromD && d <= toD;
    }
    return [
      'mrp-month',
      isMonthDisabled(y, idx) ? 'is-disabled' : '',
      isStart ? 'is-start' : '',
      isEnd ? 'is-end' : '',
      inRange ? 'is-inrange' : ''
    ].join(' ').trim();
  };

  const LeftBoard = () => (
    <div className="mrp-board">
      <div className="mrp-board-head">
        <button className="mrp-icon-btn" onClick={() => shiftLeftYear(-1)} aria-label="Prev year">
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <div className="mrp-year">{leftYear}</div>
        <button className="mrp-icon-btn" onClick={() => shiftLeftYear(+1)} aria-label="Next year">
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>
      <div className="mrp-months">
        {MONTHS.map((label, idx) => (
          <button
            key={`L-${label}`}
            className={monthBtnClass(leftYear, idx)}
            onClick={() => pickMonth(leftYear, idx)}
            type="button"
            disabled={isMonthDisabled(leftYear, idx)}
            aria-disabled={isMonthDisabled(leftYear, idx)}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  const RightBoard = () => (
    <div className="mrp-board">
      <div className="mrp-board-head">
        <button className="mrp-icon-btn" onClick={() => shiftRightYear(-1)} aria-label="Prev year">
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <div className="mrp-year">{rightYear}</div>
        <button className="mrp-icon-btn" onClick={() => shiftRightYear(+1)} aria-label="Next year">
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>
      <div className="mrp-months">
        {MONTHS.map((label, idx) => (
          <button
            key={`R-${label}`}
            className={monthBtnClass(rightYear, idx)}
            onClick={() => pickMonth(rightYear, idx)}
            type="button"
            disabled={isMonthDisabled(rightYear, idx)}
            aria-disabled={isMonthDisabled(rightYear, idx)}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="mrp" ref={rootRef}>
      {/* trigger box styled like other inputs */}
      <button
        type="button"
        className="anl-input mrp-input"
        onClick={() => setIsOpen(v => !v)}
        aria-expanded={isOpen}
      >
        <FontAwesomeIcon icon={faCalendarAlt} className="mrp-input-ic" />
        <span className={`mrp-input-text ${display ? '' : 'placeholder'}`}>
          {display || placeholder}
        </span>
      </button>

      {isOpen && (
        <div className="mrp-pop">
          <div className="mrp-boards">
            <LeftBoard />
            <RightBoard />
          </div>

          <div className="mrp-right">
            <div className="mrp-presets">
              <button type="button" onClick={() => setPreset(3)}>Last 3 months</button>
              <button type="button" onClick={() => setPreset(6)}>Last 6 months</button>
              <button type="button" onClick={() => setPreset(12)}>Last 12 months</button>
            </div>
            <div className="mrp-actions">
              <button type="button" className="action-btn" onClick={reset}>Reset</button>
              <button
                type="button"
                className="anl-btn-primary"
                disabled={!tempFrom || !tempTo}
                onClick={apply}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
